/**
 * 성능 최적화 유틸리티
 * JavaScript 실행을 최적화하고 메인 스레드 블로킹을 줄입니다.
 */

// 디바운스 함수 - 과도한 함수 호출 방지
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

// 쓰로틀 함수 - 일정 시간 간격으로만 함수 실행
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

// requestIdleCallback polyfill
const requestIdleCallbackPolyfill = (callback: IdleRequestCallback): number => {
  const start = Date.now();
  
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    } as IdleDeadline);
  }, 1) as unknown as number;
};

const cancelIdleCallbackPolyfill = (id: number): void => {
  clearTimeout(id);
};

export const requestIdleCallbackSafe = 
  typeof window !== 'undefined' && window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : requestIdleCallbackPolyfill;

export const cancelIdleCallbackSafe = 
  typeof window !== 'undefined' && window.cancelIdleCallback
    ? window.cancelIdleCallback.bind(window)
    : cancelIdleCallbackPolyfill;

// 무거운 작업을 청크로 나누어 실행
export const chunkWork = async <T>(
  items: T[],
  processItem: (item: T, index: number) => void | Promise<void>,
  chunkSize: number = 10
): Promise<void> => {
  const chunks: T[][] = [];
  
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  
  for (const chunk of chunks) {
    await new Promise<void>((resolve) => {
      requestIdleCallbackSafe(async () => {
        for (let i = 0; i < chunk.length; i++) {
          await processItem(chunk[i], i);
        }
        resolve();
      });
    });
  }
};

// 메모이제이션 함수
export const memoize = <T extends (...args: any[]) => any>(
  fn: T,
  getKey?: (...args: Parameters<T>) => string
): T => {
  const cache = new Map<string, ReturnType<T>>();
  
  return ((...args: Parameters<T>) => {
    const key = getKey ? getKey(...args) : JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    
    const result = fn(...args);
    cache.set(key, result);
    
    // 캐시 크기 제한 (100개)
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value;
      if (firstKey !== undefined) {
        cache.delete(firstKey);
      }
    }
    
    return result;
  }) as T;
};

// Virtual Scrolling 헬퍼
export const calculateVisibleItems = <T>(
  items: T[],
  scrollTop: number,
  containerHeight: number,
  itemHeight: number,
  overscan: number = 3
): { visibleItems: T[], startIndex: number, endIndex: number } => {
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(
    items.length - 1,
    Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
  );
  
  return {
    visibleItems: items.slice(startIndex, endIndex + 1),
    startIndex,
    endIndex
  };
};

// 프리로드 리소스
export const preloadResource = (url: string, type: 'image' | 'script' | 'style' | 'font'): void => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = url;
  
  switch (type) {
    case 'image':
      link.as = 'image';
      break;
    case 'script':
      link.as = 'script';
      break;
    case 'style':
      link.as = 'style';
      break;
    case 'font':
      link.as = 'font';
      link.crossOrigin = 'anonymous';
      break;
  }
  
  document.head.appendChild(link);
};

// 프리커넥트 도메인
export const preconnectDomain = (domain: string): void => {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = domain;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

// DNS 프리페치
export const dnsPrefetch = (domain: string): void => {
  const link = document.createElement('link');
  link.rel = 'dns-prefetch';
  link.href = domain;
  document.head.appendChild(link);
};

// 초기화 함수 - 앱 시작 시 실행
export const initializePerformanceOptimizations = (): void => {
  // Supabase 도메인 프리커넥트
  preconnectDomain('https://yxlgjkwlqogjzcdvbsum.supabase.co');
  
  // CDN 도메인 DNS 프리페치
  dnsPrefetch('https://cdn.jsdelivr.net');
  
  // 웹 폰트 프리로드 (있는 경우)
  // preloadResource('/fonts/your-font.woff2', 'font');
};

export default {
  debounce,
  throttle,
  requestIdleCallbackSafe,
  cancelIdleCallbackSafe,
  chunkWork,
  memoize,
  calculateVisibleItems,
  preloadResource,
  preconnectDomain,
  dnsPrefetch,
  initializePerformanceOptimizations
};